import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  useGetPositionLeadersQuery,
  useSearchPlayersQuery,
} from "../../store/api/mlbApi";
import { formatPlayerName } from "../../utils/textUtils";
import { formatPositionLeaderValue } from "../../utils/fanPage";
import { colors } from "../../theme";
import PlayerPickRow from "./PlayerPickRow";

const defaultSortForSlot = (slotId) => (slotId === "P" ? "wins" : "ops");

const PlayerPickerDialog = ({
  open,
  title = "Pick a player",
  slotId,
  excludePlayerIds = [],
  onClose,
  onSelect,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sort, setSort] = useState(defaultSortForSlot(slotId));

  useEffect(() => {
    if (!open) {
      setInputValue("");
      setSearchTerm("");
    }
    setSort(defaultSortForSlot(slotId));
  }, [open, slotId]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(inputValue.trim()), 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const searching = searchTerm.length >= 2;
  const { data, isFetching } = useSearchPlayersQuery(
    searching ? searchTerm : null,
    { skip: !open || !searching }
  );
  const { data: leaders, isFetching: loadingLeaders } =
    useGetPositionLeadersQuery(
      { slot: slotId, sort },
      { skip: !open || !slotId }
    );

  const excluded = useMemo(() => new Set(excludePlayerIds), [excludePlayerIds]);
  const options = (data || []).filter((player) => !excluded.has(player.playerId));
  const suggestions = (leaders?.players || []).filter(
    (player) => !excluded.has(player.playerId)
  );
  const showSuggestions = Boolean(slotId) && !searching;
  const sortOptions = leaders?.sortOptions || [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ overflow: "visible", pt: 1 }}>
        <Autocomplete
          autoFocus
          options={options}
          loading={isFetching}
          value={null}
          inputValue={inputValue}
          open={searching}
          onInputChange={(_, value, reason) => {
            if (reason === "reset" || reason === "selectOption") return;
            setInputValue(value);
          }}
          onChange={(_, player) => {
            if (player?.playerId) {
              onSelect(player);
              setInputValue("");
              setSearchTerm("");
            }
          }}
          getOptionLabel={(option) => formatPlayerName(option) || ""}
          isOptionEqualToValue={(option, value) =>
            option?.playerId === value?.playerId
          }
          filterOptions={(items) => items}
          noOptionsText={
            searching
              ? "No players found"
              : "Type at least 2 letters to search everyone"
          }
          renderOption={(props, option) => {
            const { key, ...rowProps } = props;
            const nameMeta = [option?.bio?.team, option?.bio?.position]
              .filter(Boolean)
              .join(" · ");
            return (
              <Box component="li" key={option.playerId || key} {...rowProps}>
                <PlayerPickRow player={option} meta={nameMeta} />
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              placeholder="Search the full player database"
              margin="dense"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {isFetching ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {showSuggestions && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: "0.12em" }}
            >
              {slotId === "H" ? "Top hitters" : "Top at this position"}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 1 }}
            >
              {slotId === "H"
                ? "Enough career at-bats, ranked by the stat you pick."
                : "Played enough here, ranked by the stat you pick."}
            </Typography>
            {sortOptions.length > 0 && (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={sort}
                onChange={(_, value) => {
                  if (value) setSort(value);
                }}
                sx={{
                  mb: 1.5,
                  flexWrap: "wrap",
                  "& .MuiToggleButton-root": {
                    px: 1.25,
                    py: 0.25,
                    textTransform: "none",
                    borderColor: colors.border,
                  },
                }}
              >
                {sortOptions.map((option) => (
                  <ToggleButton key={option.id} value={option.id}>
                    {option.abbrev || option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
            {loadingLeaders ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              suggestions.map((player) => (
                <PlayerPickRow
                  key={player.playerId}
                  player={player}
                  meta={formatPositionLeaderValue(leaders.metric, player.value)}
                  onClick={onSelect}
                />
              ))
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PlayerPickerDialog;
